// Pure size maths for the corner drag-resize handle in the logo editor
// (flag REACT_APP_LOGO_DRAG_RESIZE). Everything here returns integer mm
// with the aspect ratio locked, exactly what the size fields produce today
// (LogoResize: Math.round + a fixed aspect per gesture) and what the resize
// endpoint stores ((int) width/height).

import {
  correctLogoSizeForTransferMethod,
  doesLogoFitInTransferMethod,
} from "../../../../utils/functions/SizeUtil";

export interface SizeMm {
  widthMm: number;
  heightMm: number;
}

export interface DragSizeInput {
  // Size (form) when the drag started - the aspect is locked to this pair.
  startWidthMm: number;
  startHeightMm: number;
  // Painted bitmap size in CSS px when the drag started.
  paintedWidthPx: number;
  paintedHeightPx: number;
  // Pointer displacement since pointer-down, in CSS px.
  dx: number;
  dy: number;
  // Transfer method maximum "WxH" (rotation allowed); null/undefined = no cap.
  methodSize?: string | null;
}

function parseMethodSize(
  methodSize?: string | null
): { maxWidth: number; maxHeight: number } | null {
  if (!methodSize || !methodSize.includes("x")) return null;
  const [maxWidth, maxHeight] = methodSize.split("x").map(Number);
  if (
    isNaN(maxWidth) ||
    isNaN(maxHeight) ||
    maxWidth <= 0 ||
    maxHeight <= 0
  ) {
    return null;
  }
  return { maxWidth, maxHeight };
}

/**
 * Largest uniform scale of (w0, h0) that still fits the method maximum in
 * either orientation (the same two orientations doesLogoFitInTransferMethod
 * accepts). Infinity without a usable method size - typing has no cap
 * either.
 */
export function maxScaleForMethod(
  w0: number,
  h0: number,
  methodSize?: string | null
): number {
  const limits = parseMethodSize(methodSize);
  if (!limits || !(w0 > 0) || !(h0 > 0)) return Infinity;
  const normal = Math.min(limits.maxWidth / w0, limits.maxHeight / h0);
  const rotated = Math.min(limits.maxHeight / w0, limits.maxWidth / h0);
  return Math.max(normal, rotated);
}

/** Smallest scale that keeps the shortest side at >= 1 mm. */
export function minScaleFor(w0: number, h0: number): number {
  const shortest = Math.min(w0, h0);
  return shortest > 0 ? 1 / shortest : 1;
}

/**
 * Last-resort cap: integer rounding of the width can push the derived
 * height 1 mm over the method maximum. Shrinks to the corrected size
 * (floored so the result always fits) while keeping the locked aspect.
 */
export function fitToMethod(
  widthMm: number,
  heightMm: number,
  aspect: number,
  methodSize?: string | null
): SizeMm {
  if (
    !parseMethodSize(methodSize) ||
    doesLogoFitInTransferMethod(widthMm, heightMm, methodSize as string)
  ) {
    return { widthMm, heightMm };
  }
  const corrected = correctLogoSizeForTransferMethod(
    widthMm,
    heightMm,
    methodSize as string
  );
  const w = Math.max(1, Math.floor(corrected.width));
  let h = Math.max(1, Math.round(w / aspect));
  if (!doesLogoFitInTransferMethod(w, h, methodSize as string)) {
    h = Math.max(1, Math.floor(corrected.height));
  }
  return { widthMm: w, heightMm: h };
}

/**
 * Size for the current pointer position of a corner drag: the displacement
 * is projected on the bitmap diagonal (dragging along the diagonal by its
 * own length doubles the size; dragging across it changes nothing), the
 * scale is clamped to [1 mm on the shortest side, method maximum] and the
 * result is rounded to integer mm with the START aspect - the orientation
 * never flips mid-drag.
 */
export function computeDragSize(input: DragSizeInput): SizeMm {
  const {
    startWidthMm: w0,
    startHeightMm: h0,
    paintedWidthPx: W,
    paintedHeightPx: H,
    dx,
    dy,
    methodSize,
  } = input;
  if (!(w0 > 0) || !(h0 > 0) || !(W > 0) || !(H > 0)) {
    return { widthMm: w0, heightMm: h0 };
  }
  const t = (dx * W + dy * H) / (W * W + H * H);
  const scale = Math.min(
    maxScaleForMethod(w0, h0, methodSize),
    Math.max(minScaleFor(w0, h0), 1 + t)
  );
  const aspect = w0 / h0;
  const widthMm = Math.max(1, Math.round(w0 * scale));
  const heightMm = Math.max(1, Math.round(widthMm / aspect));
  return fitToMethod(widthMm, heightMm, aspect, methodSize);
}

/**
 * Keyboard nudge: width +/- deltaMm, height follows the locked aspect, never
 * below 1 mm. At the method maximum the size stops (returns the current
 * size) instead of jumping around the cap.
 */
export function stepSize(
  current: SizeMm,
  deltaMm: number,
  aspect: number,
  methodSize?: string | null
): SizeMm {
  const safeAspect = aspect > 0 && Number.isFinite(aspect) ? aspect : 1;
  const widthMm = Math.max(1, Math.round(current.widthMm + deltaMm));
  const heightMm = Math.max(1, Math.round(widthMm / safeAspect));
  if (
    parseMethodSize(methodSize) &&
    !doesLogoFitInTransferMethod(widthMm, heightMm, methodSize as string)
  ) {
    return doesLogoFitInTransferMethod(
      current.widthMm,
      current.heightMm,
      methodSize as string
    )
      ? { widthMm: current.widthMm, heightMm: current.heightMm }
      : fitToMethod(widthMm, heightMm, safeAspect, methodSize);
  }
  return { widthMm, heightMm };
}

/** Parity with LogoResize: sizes of 30x30 mm or less ask for confirmation. */
export function needsSmallSizeConfirm(
  widthMm: number,
  heightMm: number
): boolean {
  return widthMm <= 30 && heightMm <= 30;
}
